import { create } from "zustand";
import {
  AppDocument,
  DiscoverySource,
  FollowUpReminder,
  HiddenSignal,
  HiddenSignalStatus,
  Lead,
  Opportunity,
  OutreachDraft,
  OutreachDraftChannel,
  OutreachDraftStatus,
  PropertySnapshot,
} from "../types";
import { v4 as uuidv4 } from "uuid";
import { doc, setDoc, updateDoc, deleteDoc } from "firebase/firestore";
import { auth, db, userCollectionPath } from "../lib/firebase";
import { geocodeAddress } from "../lib/geocoding";
import { handleFirestoreError, OperationType } from "../lib/firestore-errors";

type SavePropertySnapshotInput = Omit<
  PropertySnapshot,
  "id" | "userId" | "capturedAt" | "tags" | "notes" | "source"
> & {
  tags?: string[];
  notes?: string;
  source?: DiscoverySource;
};

type CreateHiddenSignalInput = Omit<
  HiddenSignal,
  "id" | "userId" | "detectedAt" | "status" | "promotedOpportunityId" | "tags" | "notes" | "source"
> & {
  tags?: string[];
  notes?: string;
  source?: DiscoverySource;
  status?: HiddenSignalStatus;
};

type CreateOutreachDraftInput =
  | {
      originType: "lead";
      lead: Lead;
      channel?: OutreachDraftChannel;
      subject?: string;
      message?: string;
      status?: OutreachDraftStatus;
      source?: DiscoverySource;
    }
  | {
      originType: "signal";
      signal: HiddenSignal;
      channel?: OutreachDraftChannel;
      subject?: string;
      message?: string;
      status?: OutreachDraftStatus;
      source?: DiscoverySource;
    };

export interface ApiKeys {
  gemini?: string;
  openai?: string;
  claude?: string;
  ollama?: string;
  qwen?: string;
  opencode?: string;
  openrouter?: string;
}

interface AppState {
  userId: string | null;
  isAuthenticated: boolean;
  hasCompletedOnboarding: boolean;
  isPro: boolean;
  planId: "free" | "foundation" | "operator" | "atlas";
  businessName: string;
  focus: string;
  followUpStyle: string;
  cadence: string;
  apiKeys: ApiKeys;
  activeAiProvider: string;
  selectedModels: Record<string, string>;
  userEmail: string | null;
  setAuth: (auth: boolean, userId: string | null, userEmail?: string | null) => void;
  setProStatus: (status: boolean) => Promise<void>;
  setPlanId: (planId: "free" | "foundation" | "operator" | "atlas") => Promise<void>;
  setOnboardingData: (data: { businessName: string; focus: string; followUpStyle: string; cadence: string; activeAiProvider?: string; selectedModels?: Record<string, string> }, completed: boolean) => void;
  loadOnboardingData: (data: { businessName: string; focus: string; followUpStyle: string; cadence: string; isPro?: boolean; planId?: "free" | "foundation" | "operator" | "atlas"; apiKeys?: ApiKeys; activeAiProvider?: string; selectedModels?: Record<string, string> }, completed: boolean) => void;
  setApiKeys: (keys: ApiKeys) => Promise<void>;
  setSelectedModel: (provider: string, model: string) => Promise<void>;
  setLeads: (leads: Lead[]) => void;
  propertySnapshots: PropertySnapshot[];
  hiddenSignals: HiddenSignal[];
  outreachDrafts: OutreachDraft[];
  setPropertySnapshots: (snapshots: PropertySnapshot[]) => void;
  setHiddenSignals: (signals: HiddenSignal[]) => void;
  setOutreachDrafts: (drafts: OutreachDraft[]) => void;
  clearStore: () => void;
  leads: Lead[];
  opportunities: Opportunity[];
  documents: AppDocument[];
  addLead: (lead: Omit<Lead, "id" | "createdAt" | "updatedAt" | "followUps" | "activities" | "userId">) => Promise<void>;
  updateLead: (id: string, updates: Partial<Lead>) => Promise<void>;
  deleteLead: (id: string) => Promise<void>;
  setOpportunities: (opportunities: Opportunity[]) => void;
  addOpportunity: (opportunity: Omit<Opportunity, "id" | "savedAt" | "userId" | "lat" | "lng" | "geohash">) => Promise<boolean>;
  savePropertySnapshot: (snapshot: SavePropertySnapshotInput) => Promise<void>;
  addHiddenSignal: (signal: CreateHiddenSignalInput) => Promise<void>;
  promoteSignalToOpportunity: (signalId: string) => Promise<void>;
  createOutreachDraft: (input: CreateOutreachDraftInput) => Promise<void>;
  updateOutreachDraft: (id: string, updates: Partial<OutreachDraft>) => Promise<void>;
  deleteOutreachDraft: (id: string) => Promise<void>;
  setDocuments: (documents: AppDocument[]) => void;
  addDocument: (doc: Omit<AppDocument, "id" | "uploadedAt" | "userId">) => Promise<AppDocument | null>;
  updateDocument: (id: string, updates: Partial<AppDocument>) => Promise<void>;
  deleteDocument: (id: string) => Promise<void>;
  addFollowUp: (leadId: string, dueDate: string, notes?: string) => Promise<void>;
  completeFollowUp: (leadId: string, followUpId: string) => Promise<void>;
  addActivity: (leadId: string, type: import("../types").ActivityType, content: string) => Promise<void>;
}

export const useAppStore = create<AppState>()((set, get) => ({
  userId: null,
  isAuthenticated: false,
  hasCompletedOnboarding: false,
  isPro: false,
  planId: "free",
  businessName: "",
  focus: "",
  followUpStyle: "casual",
  cadence: "2",
  activeAiProvider: "gemini",
  selectedModels: {},
  userEmail: null,
  apiKeys: {},
  leads: [],
  opportunities: [],
  propertySnapshots: [],
  hiddenSignals: [],
  outreachDrafts: [],
  documents: [],

  setAuth: (authStatus, userId, userEmail = null) => set({ isAuthenticated: authStatus, userId, userEmail }),
  
  setProStatus: async (status: boolean) => {
    const { userId } = get();
    set({ isPro: status, planId: status ? "foundation" : "free" });
    if (userId) {
      try {
        await updateDoc(doc(db, "users", userId), { isPro: status, planId: status ? "foundation" : "free" });
      } catch (error) {
        handleFirestoreError(error, OperationType.UPDATE, `users/${userId}`);
      }
    }
  },

  setPlanId: async (planId) => {
    const { userId } = get();
    const isPro = planId !== "free";
    set({ planId, isPro });
    if (userId) {
      try {
        await updateDoc(doc(db, "users", userId), { planId, isPro });
      } catch (error) {
        handleFirestoreError(error, OperationType.UPDATE, `users/${userId}`);
      }
    }
  },

  setApiKeys: async (keys) => {
    const { userId, apiKeys } = get();
    const mergedKeys = Object.fromEntries(
      Object.entries({ ...apiKeys, ...keys }).map(([provider, value]) => {
        const currentValue = apiKeys[provider as keyof ApiKeys];
        if (typeof value === "string") {
          const trimmed = value.trim();
          if (trimmed) {
            return [provider, trimmed];
          }
          return [provider, currentValue || ""];
        }
        return [provider, currentValue || ""];
      }),
    ) as ApiKeys;

    set({ apiKeys: mergedKeys });
    if (userId) {
      try {
        await updateDoc(doc(db, "users", userId), { apiKeys: mergedKeys });
      } catch (error) {
        handleFirestoreError(error, OperationType.UPDATE, `users/${userId}`);
      }
    }
  },

  setSelectedModel: async (provider: string, model: string) => {
    const { userId, selectedModels } = get();
    const newSelectedModels = { ...selectedModels, [provider]: model };
    set({ selectedModels: newSelectedModels });
    if (userId) {
      try {
        await updateDoc(doc(db, "users", userId), { selectedModels: newSelectedModels });
      } catch (error) {
        handleFirestoreError(error, OperationType.UPDATE, `users/${userId}`);
      }
    }
  },

  setOnboardingData: async (data, completed) => {
    const { userId, hasCompletedOnboarding } = get();
    set({
      ...data,
      hasCompletedOnboarding: completed,
    });

    if (userId) {
       try {
         if (hasCompletedOnboarding || completed === false) {
           // Either already onboarding, or updating via settings. Use updateDoc to avoid sending redundant fields.
           await updateDoc(doc(db, "users", userId), {
             ...data,
             hasCompletedOnboarding: completed,
           });
         } else {
           // First time completing onboarding
           await setDoc(doc(db, "users", userId), {
             uid: userId,
             email: auth.currentUser?.email || "",
             ...data,
             hasCompletedOnboarding: completed,
             createdAt: new Date().toISOString()
           });
         }
       } catch (error) {
         handleFirestoreError(error, OperationType.CREATE, `users/${userId}`);
       }
    }
  },

  loadOnboardingData: (data, completed) => {
    set({
      ...data,
      hasCompletedOnboarding: completed,
      isPro: data.planId ? data.planId !== "free" : data.isPro || false,
      planId: data.planId || (data.isPro ? "foundation" : "free"),
      apiKeys: data.apiKeys || {},
      activeAiProvider: data.activeAiProvider || "gemini",
      selectedModels: data.selectedModels || {},
    });
  },

  setLeads: (leads) => set({ leads }),
  setOpportunities: (opportunities) => set({ opportunities }),
  setPropertySnapshots: (propertySnapshots) => set({ propertySnapshots }),
  setHiddenSignals: (hiddenSignals) => set({ hiddenSignals }),
  setOutreachDrafts: (outreachDrafts) => set({ outreachDrafts }),
  
  clearStore: () => set({
    userId: null,
    isAuthenticated: false,
    hasCompletedOnboarding: false,
    isPro: false,
    planId: "free",
    leads: [],
    opportunities: [],
    propertySnapshots: [],
    hiddenSignals: [],
    outreachDrafts: [],
    businessName: "",
    focus: "",
    followUpStyle: "casual",
    cadence: "2",
    activeAiProvider: "gemini",
    selectedModels: {},
    apiKeys: {},
    userEmail: null,
  }),

  addOpportunity: async (opportunityData) => {
    const { userId } = get();
    if (!userId) return false;
    const normalizedAddress = opportunityData.propertyAddress.trim().toLowerCase();
    const existing = get().opportunities.some((opp) => opp.propertyAddress.trim().toLowerCase() === normalizedAddress);
    if (existing) {
      return false;
    }
    const id = uuidv4();
    
    // Geocode to get lat/lng/hash
    const geocode = await geocodeAddress(opportunityData.propertyAddress);
    
    const newOpportunity: Opportunity = {
      ...opportunityData,
      id,
      userId,
      savedAt: new Date().toISOString(),
      ...(geocode ? { lat: geocode.lat, lng: geocode.lng, geohash: geocode.hash } : {})
    };
    set((state) => ({ opportunities: [newOpportunity, ...state.opportunities] }));
    try {
      await setDoc(doc(db, `${userCollectionPath(userId, "opportunities")}/${id}`), newOpportunity);
      return true;
    } catch (error) {
       handleFirestoreError(error, OperationType.CREATE, `${userCollectionPath(userId, "opportunities")}/${id}`);
      return false;
    }
  },

  savePropertySnapshot: async (snapshotData) => {
    const { userId, propertySnapshots } = get();
    if (!userId) return;

    const id = uuidv4();
    const geocode = await geocodeAddress(snapshotData.propertyAddress);
    const capturedAt = new Date().toISOString();
    const snapshot: PropertySnapshot = {
      ...snapshotData,
      id,
      userId,
      capturedAt,
      tags: snapshotData.tags || [],
      notes: snapshotData.notes || "",
      source: snapshotData.source || snapshotData.originType,
      ...(geocode ? { lat: geocode.lat, lng: geocode.lng, geohash: geocode.hash } : {}),
    };

    set({ propertySnapshots: [snapshot, ...propertySnapshots] });

    try {
      await setDoc(doc(db, `${userCollectionPath(userId, "propertySnapshots")}/${id}`), snapshot);
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, `${userCollectionPath(userId, "propertySnapshots")}/${id}`);
    }
  },

  addHiddenSignal: async (signalData) => {
    const { userId, hiddenSignals } = get();
    if (!userId) return;

    const id = uuidv4();
    const geocode = await geocodeAddress(signalData.propertyAddress);
    const detectedAt = new Date().toISOString();
    const signal: HiddenSignal = {
      ...signalData,
      id,
      userId,
      detectedAt,
      status: signalData.status || "new",
      tags: signalData.tags || [],
      notes: signalData.notes || "",
      source: signalData.source || signalData.originType,
      ...(geocode ? { lat: geocode.lat, lng: geocode.lng, geohash: geocode.hash } : {}),
    };

    set({ hiddenSignals: [signal, ...hiddenSignals] });

    try {
      await setDoc(doc(db, `${userCollectionPath(userId, "hiddenSignals")}/${id}`), signal);
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, `${userCollectionPath(userId, "hiddenSignals")}/${id}`);
    }
  },

  promoteSignalToOpportunity: async (signalId) => {
    const { userId, hiddenSignals, opportunities } = get();
    if (!userId) return;

    const signal = hiddenSignals.find((entry) => entry.id === signalId);
    if (!signal) return;

    const id = uuidv4();
    const geocode = signal.lat && signal.lng && signal.geohash ? null : await geocodeAddress(signal.propertyAddress);
    const savedAt = new Date().toISOString();
    const opportunity: Opportunity = {
      id,
      userId,
      propertyAddress: signal.propertyAddress,
      tags: signal.tags || [],
      opportunityScore: Math.max(0, Math.min(100, signal.severity === "critical" ? 95 : signal.severity === "high" ? 80 : signal.severity === "medium" ? 65 : 50)),
      source: signal.source === "lead" ? "lead" : "signal",
      notes: [signal.title, signal.description, signal.notes].filter(Boolean).join("\n\n"),
      savedAt,
      signalId: signal.id,
      ...(signal.snapshotId ? { snapshotId: signal.snapshotId } : {}),
      ...(signal.lat !== undefined ? { lat: signal.lat } : geocode ? { lat: geocode.lat } : {}),
      ...(signal.lng !== undefined ? { lng: signal.lng } : geocode ? { lng: geocode.lng } : {}),
      ...(signal.geohash ? { geohash: signal.geohash } : geocode ? { geohash: geocode.hash } : {}),
    };

    const updatedSignal: HiddenSignal = {
      ...signal,
      status: "promoted",
      promotedOpportunityId: id,
    };

    set({
      opportunities: [opportunity, ...opportunities],
      hiddenSignals: hiddenSignals.map((entry) => (entry.id === signalId ? updatedSignal : entry)),
    });

    try {
      await setDoc(doc(db, `${userCollectionPath(userId, "opportunities")}/${id}`), opportunity);
      await updateDoc(doc(db, `${userCollectionPath(userId, "hiddenSignals")}/${signalId}`), {
        status: "promoted",
        promotedOpportunityId: id,
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `${userCollectionPath(userId, "hiddenSignals")}/${signalId}`);
    }
  },

  createOutreachDraft: async (input) => {
    const { userId, outreachDrafts } = get();
    if (!userId) return;

    const id = uuidv4();
    const now = new Date().toISOString();

    const baseDraft = input.originType === "lead"
      ? {
          propertyAddress: input.lead.propertyAddress,
          tags: input.lead.tags || [],
          notes: input.lead.notes || "",
          source: input.source || input.lead.source || "lead",
          originId: input.lead.id,
          leadId: input.lead.id,
          signalId: undefined,
          subject: input.subject || `Follow up on ${input.lead.propertyAddress}`,
          message:
            input.message ||
            `Hi ${input.lead.sellerName || "there"}, I wanted to reach out about ${input.lead.propertyAddress}.`,
        }
      : {
          propertyAddress: input.signal.propertyAddress,
          tags: input.signal.tags || [],
          notes: input.signal.notes || input.signal.description || "",
          source: input.source || input.signal.source || "signal",
          originId: input.signal.id,
          leadId: input.signal.relatedLeadId,
          signalId: input.signal.id,
          subject: input.subject || `Outreach for ${input.signal.propertyAddress}`,
          message:
            input.message ||
            `Hi, I noticed ${input.signal.propertyAddress} and wanted to see if you'd be open to a quick conversation.`,
        };

    const draft: OutreachDraft = {
      id,
      userId,
      propertyAddress: baseDraft.propertyAddress,
      tags: baseDraft.tags,
      notes: baseDraft.notes,
      source: baseDraft.source as DiscoverySource,
      originType: input.originType,
      originId: baseDraft.originId,
      channel: input.channel || "sms",
      status: input.status || "draft",
      subject: baseDraft.subject,
      message: baseDraft.message,
      createdAt: now,
      updatedAt: now,
      ...(baseDraft.leadId ? { leadId: baseDraft.leadId } : {}),
      ...(baseDraft.signalId ? { signalId: baseDraft.signalId } : {}),
    };

    set({ outreachDrafts: [draft, ...outreachDrafts] });

    try {
      await setDoc(doc(db, `${userCollectionPath(userId, "outreachDrafts")}/${id}`), draft);
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, `${userCollectionPath(userId, "outreachDrafts")}/${id}`);
    }
  },

  updateOutreachDraft: async (id, updates) => {
    const { userId, outreachDrafts } = get();
    if (!userId) return;

    const nextDrafts = outreachDrafts.map((draft) =>
      draft.id === id ? { ...draft, ...updates, updatedAt: new Date().toISOString() } : draft
    );
    const updatedDraft = nextDrafts.find((draft) => draft.id === id);
    if (!updatedDraft) return;

    set({ outreachDrafts: nextDrafts });

    try {
      await updateDoc(doc(db, `${userCollectionPath(userId, "outreachDrafts")}/${id}`), {
        ...updates,
        updatedAt: updatedDraft.updatedAt,
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `${userCollectionPath(userId, "outreachDrafts")}/${id}`);
    }
  },

  deleteOutreachDraft: async (id) => {
    const { userId, outreachDrafts } = get();
    if (!userId) return;

    set({ outreachDrafts: outreachDrafts.filter((draft) => draft.id !== id) });

    try {
      await deleteDoc(doc(db, `${userCollectionPath(userId, "outreachDrafts")}/${id}`));
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, `${userCollectionPath(userId, "outreachDrafts")}/${id}`);
    }
  },

  addLead: async (leadData) => {
    const { userId } = get();
    if (!userId) return;
    const id = uuidv4();

    // Geocode to get lat/lng/hash
    const geocode = await geocodeAddress(leadData.propertyAddress);

    const newLead: Lead = {
      ...leadData,
      id,
      userId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ...(geocode ? { lat: geocode.lat, lng: geocode.lng, geohash: geocode.hash } : {}),
      followUps: [],
      activities: [{
        id: uuidv4(),
        leadId: id,
        type: "note",
        content: "Lead created",
        timestamp: new Date().toISOString()
      }],
    };
    
    // Optimistic update
    set((state) => ({ leads: [newLead, ...state.leads] }));
    
    try {
      await setDoc(doc(db, `${userCollectionPath(userId, "leads")}/${id}`), newLead);
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, `${userCollectionPath(userId, "leads")}/${id}`);
    }
  },

  updateLead: async (id, updates) => {
    const { userId, leads } = get();
    if (!userId) return;
    
    const lead = leads.find(l => l.id === id);
    if (!lead) return;

    let newActivities = lead.activities || [];
    if (updates.status && updates.status !== lead.status) {
      newActivities = [{
        id: uuidv4(),
        leadId: id,
        type: "status_change",
        content: `Status changed from ${lead.status} to ${updates.status}`,
        timestamp: new Date().toISOString()
      }, ...newActivities];
      updates.activities = newActivities;
    }

    const newLead = { ...lead, ...updates, activities: newActivities, updatedAt: new Date().toISOString() };
    
    // Optimistic
    set((state) => ({
      leads: state.leads.map((l) => (l.id === id ? newLead : l)),
    }));

    try {
      await updateDoc(doc(db, `${userCollectionPath(userId, "leads")}/${id}`), {
        ...updates,
        updatedAt: newLead.updatedAt,
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `${userCollectionPath(userId, "leads")}/${id}`);
    }
  },

  deleteLead: async (id) => {
    const { userId } = get();
    if (!userId) return;
    
    set((state) => ({ leads: state.leads.filter((l) => l.id !== id) }));
    
    try {
      await deleteDoc(doc(db, `${userCollectionPath(userId, "leads")}/${id}`));
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, `${userCollectionPath(userId, "leads")}/${id}`);
    }
  },

  addFollowUp: async (leadId, dueDate, notes) => {
    const { userId, leads } = get();
    if (!userId) return;
    
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;

    const newFollowUp: FollowUpReminder = { id: uuidv4(), dueDate, notes, completed: false };
    const newFollowUps = [...lead.followUps, newFollowUp];
    const newUpdatedAt = new Date().toISOString();

    const newActivity = {
      id: uuidv4(),
      leadId,
      type: "note" as const,
      content: `Follow-up scheduled for ${new Date(dueDate).toLocaleDateString()}${notes ? ` - ${notes}` : ""}`,
      timestamp: newUpdatedAt
    };
    const newActivities = [newActivity, ...(lead.activities || [])];

    set((state) => ({
      leads: state.leads.map((l) => l.id === leadId ? { ...l, followUps: newFollowUps, activities: newActivities, updatedAt: newUpdatedAt } : l),
    }));

    try {
      await updateDoc(doc(db, `${userCollectionPath(userId, "leads")}/${leadId}`), {
        followUps: newFollowUps,
        activities: newActivities,
        updatedAt: newUpdatedAt,
      });
    } catch (error) {
       handleFirestoreError(error, OperationType.UPDATE, `${userCollectionPath(userId, "leads")}/${leadId}`);
    }
  },

  completeFollowUp: async (leadId, followUpId) => {
    const { userId, leads } = get();
    if (!userId) return;
    
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;

    const followUpToComplete = lead.followUps.find(f => f.id === followUpId);
    const newFollowUps = lead.followUps.map(fu => fu.id === followUpId ? { ...fu, completed: true } : fu);
    const newUpdatedAt = new Date().toISOString();

    const newActivity = {
      id: uuidv4(),
      leadId,
      type: "note" as const,
      content: `Follow-up completed${followUpToComplete?.notes ? ` - ${followUpToComplete.notes}` : ""}`,
      timestamp: newUpdatedAt
    };
    const newActivities = [newActivity, ...(lead.activities || [])];

    set((state) => ({
      leads: state.leads.map((l) => l.id === leadId ? { ...l, followUps: newFollowUps, activities: newActivities, updatedAt: newUpdatedAt } : l),
    }));

    try {
      await updateDoc(doc(db, `${userCollectionPath(userId, "leads")}/${leadId}`), {
        followUps: newFollowUps,
        activities: newActivities,
        updatedAt: newUpdatedAt,
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `${userCollectionPath(userId, "leads")}/${leadId}`);
    }
  },

  addActivity: async (leadId, type, content) => {
    const { userId, leads } = get();
    if (!userId) return;
    
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;

    const newActivity = {
      id: uuidv4(),
      leadId,
      type,
      content,
      timestamp: new Date().toISOString()
    };

    const newActivities = [newActivity, ...(lead.activities || [])];
    const newUpdatedAt = new Date().toISOString();

    set((state) => ({
      leads: state.leads.map((l) => l.id === leadId ? { ...l, activities: newActivities, updatedAt: newUpdatedAt } : l),
    }));

    try {
      await updateDoc(doc(db, `${userCollectionPath(userId, "leads")}/${leadId}`), {
        activities: newActivities,
        updatedAt: newUpdatedAt,
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `${userCollectionPath(userId, "leads")}/${leadId}`);
    }
  },
  
  setDocuments: (documents) => set({ documents }),

  addDocument: async (docData) => {
    const { userId, documents } = get();
    if (!userId) return null;
    const id = uuidv4();
    const newDoc: AppDocument = {
      ...docData,
      id,
      userId,
      uploadedAt: new Date().toISOString(),
    };

    set({ documents: [newDoc, ...documents] });

    try {
      await setDoc(doc(db, `${userCollectionPath(userId, "documents")}/${id}`), newDoc);
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, `${userCollectionPath(userId, "documents")}/${id}`);
    }

    return newDoc;
  },

  updateDocument: async (id, updates) => {
    const { userId, documents } = get();
    if (!userId) return;

    const updatedDocuments = documents.map((document) =>
      document.id === id ? { ...document, ...updates } : document
    );
    set({ documents: updatedDocuments });

    try {
      await updateDoc(doc(db, `${userCollectionPath(userId, "documents")}/${id}`), updates);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `${userCollectionPath(userId, "documents")}/${id}`);
    }
  },

  deleteDocument: async (id) => {
    const { userId, documents } = get();
    if (!userId) return;

    set({ documents: documents.filter(d => d.id !== id) });

    try {
      await deleteDoc(doc(db, `${userCollectionPath(userId, "documents")}/${id}`));
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, `${userCollectionPath(userId, "documents")}/${id}`);
    }
  },
}));
